import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function CantDecideWhatToEatPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Can't Decide What to Eat? Here's How to Pick in 60 Seconds"
        description="Can't decide what to eat? You're not indecisive — you're overwhelmed. Discover why choosing food is so hard and how Hangrily's swipe-based picker helps you decide in under a minute. Solo, couple, or group."
        path="/cant-decide-what-to-eat"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: "Can't Decide What to Eat? — Quick Food Decision Help",
            description: "Learn why you can't decide what to eat and discover a fast, fun way to pick a restaurant in under 60 seconds.",
            url: 'https://hangrily.app/cant-decide-what-to-eat'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: "Can't Decide What to Eat?", item: 'https://hangrily.app/cant-decide-what-to-eat' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: "Why can't I decide what to eat?",
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "It's not indecisiveness — it's decision fatigue. After thousands of daily choices, your brain lacks the energy to evaluate restaurant options. Add too many choices (the paradox of choice) and social pressure when deciding with others, and picking food feels impossible."
                }
              },
              {
                '@type': 'Question',
                name: 'Is there an app that decides what to eat for me?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Yes. Hangrily is a swipe-based restaurant picker that helps you decide what to eat in under a minute. It shows one restaurant at a time with photos, ratings, and distance. You swipe yes or no. The smart algorithm finds the winner based on your actual preferences — not random chance."
                }
              },
              {
                '@type': 'Question',
                name: 'How do I stop overthinking what to eat?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Limit your options first (pick a cuisine and budget), set a 5-minute timer, and use binary yes/no decisions instead of open-ended choices. Hangrily enforces all three: it filters by your preferences, completes in 2 minutes, and turns every choice into a simple swipe."
                }
              },
              {
                '@type': 'Question',
                name: 'What should I eat when nothing sounds good?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "When nothing sounds good, you need external input. Hangrily's mood-based filtering helps — tell it you're feeling 'adventurous,' 'comfort food,' or 'healthy' and it surfaces restaurants that match your emotional state, not just your cravings."
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Can't Decide What to Eat?</h1>
          <p className="hero-subtitle">
            You're not indecisive. You're just human. Here's the science — and the fix.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Why Choosing Food Feels Impossible</h2>
              <p>
                It's 7 PM. You're starving. You open your phone, scroll through 40 restaurants, read reviews, 
                check menus, ask your partner what they want, and somehow — <strong>30 minutes later</strong> — 
                you're still deciding. Then you just order pizza. Again.
              </p>
              <p>
                This isn't a character flaw. It's <strong>decision fatigue</strong> meets the 
                <strong>paradox of choice</strong> meets <strong>social negotiation</strong>. Three powerful 
                forces working against your empty stomach. The good news? There's a simple fix.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Decide What to Eat Now →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">35K+</span>
                  <span className="stat-label-sm">Decisions you make per day</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">47</span>
                  <span className="stat-label-sm">Restaurants the average person sees before giving up</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">60s</span>
                  <span className="stat-label-sm">Time to decide with Hangrily</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">0</span>
                  <span className="stat-label-sm">Arguments about dinner</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">3 Reasons You Can't Decide What to Eat</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🧠</div>
              <h3>Your Brain Is Exhausted</h3>
              <p>
                Research by psychologist Roy Baumeister shows that willpower and decision-making draw from 
                the same limited mental resource. By dinner, you've made 35,000+ decisions. Your brain 
                literally lacks the glucose and mental energy to evaluate restaurant options. That's why 
                choosing food feels harder than choosing a career path.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">📊</div>
              <h3>Too Many Options Paralyze You</h3>
              <p>
                Psychologist Barry Schwartz's "Paradox of Choice" proves that more options make people 
                <em>less</em> satisfied with their decisions. When you see 50 restaurants on Google Maps, 
                your brain freezes. Studies show satisfaction peaks at 6-10 options — anything beyond that 
                causes regret and decision paralysis. More isn't better. It's worse.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🗣️</div>
              <h3>Social Decisions Are 5x Harder</h3>
              <p>
                Choosing alone is tough. Adding another person multiplies the complexity. You have to 
                negotiate cuisines, budgets, moods, dietary restrictions, and who "wins" the decision. 
                Research shows group food decisions take 3-5 times longer than individual ones — and cause 
                way more stress. The "I don't care, you pick" loop is real.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">What to Do When Nothing Sounds Good</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Narrow the Field Immediately</h3>
                <p>
                  Don't start with "what do I want?" Start with constraints: "What cuisine am I NOT in 
                  the mood for?" Eliminate categories first. Mexican? Sure. Italian? Maybe. Indian? Not 
                  tonight. Now you're working with 3-5 cuisines instead of 20. Hangrily does this automatically 
                  — just pick your preferences and it filters everything else out.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Use Your Mood, Not Your Cravings</h3>
                <p>
                  When nothing sounds good, cravings aren't helpful. But your <em>mood</em> is. Are you 
                  feeling adventurous? Comfort-seeking? Healthy? Celebratory? Hangrily's mood filter 
                  matches your emotional state to restaurant types — so you don't have to know what you want, 
                  just how you feel.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Let an Algorithm Decide</h3>
                <p>
                  If your brain is fried, outsource the decision. Not to a random wheel — to a smart 
                  algorithm that knows your preferences. Swipe through restaurant cards yes-or-no style. 
                  In about a minute, the algorithm narrows your choices to THE answer. No overthinking. 
                  No second-guessing. Just eat.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Better Than Guessing What to Eat</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ "I'll Just Pick Something"</h3>
              <p>You stare at your phone for 20 minutes, get overwhelmed, and default to the same 3 places you always go. Zero discovery. Zero excitement.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Random Food Generator</h3>
              <p>A random wheel picks "Thai." Great, but is there even a Thai place nearby? Does it match your budget? Your group's preferences? Randomness ignores reality.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ "You Pick / No, You Pick"</h3>
              <p>The classic relationship argument. Neither person wants to decide, but both veto every suggestion. 45 minutes later you're hangry and ordering drive-thru.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily Swipe Picker</h3>
              <p>Shows real nearby restaurants matching your preferences. One at a time. Yes or no. Smart algorithm finds the winner. Works solo, with your partner, or with a group. Done in 60 seconds.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Why can't I decide what to eat?</h3>
              <p>
                Three factors: decision fatigue (your brain is exhausted from daily choices), the paradox 
                of choice (too many restaurant options overwhelm you), and social dynamics (deciding with 
                others adds negotiation complexity). It's not you — it's psychology.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is there an app that decides what to eat for me?</h3>
              <p>
                Yes. Hangrily is a swipe-based restaurant picker designed for people who can't decide what 
                to eat. It shows real nearby restaurants one at a time. You swipe yes or no. The algorithm 
                finds your match in about a minute. It works for solo dining, couples, and groups.
              </p>
            </div>
            <div className="faq-item">
              <h3>What should I eat when nothing sounds good?</h3>
              <p>
                Use your mood instead of your cravings. Are you feeling adventurous, comfort-seeking, 
                healthy, or celebratory? Hangrily's mood filter matches your emotional state to restaurant 
                types — so you don't need to know what you want, just how you feel.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I stop overthinking what to eat?</h3>
              <p>
                Set constraints first (cuisine, budget, distance), use binary yes/no decisions instead of 
                open-ended choices, and set a 2-minute time limit. Hangrily enforces all three: it pre-filters 
                by your preferences, turns every choice into a swipe, and most sessions complete in under 
                2 minutes.
              </p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Stop Staring at Your Phone. Start Eating.</h2>
          <p>Join thousands who ended the "what should I eat?" spiral. Decide in 60 seconds.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Pick What to Eat Now →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
